import * as React from 'react';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  PageSection
} from '@patternfly/react-core';
import { LockIcon } from '@patternfly/react-icons';
import { useNavigate } from 'react-router-dom';

interface ForbiddenStateProps {
  /** Replaces the default heading. */
  titleText?: string;
  /** Replaces the default explanation. */
  children?: React.ReactNode;
}

/** The subdued line above the heading, holding the status code. Matches NotFoundState. */
const codeStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--pf-t--global--font--size--md)',
  fontWeight: 'var(--pf-t--global--font--weight--body)' as React.CSSProperties['fontWeight'],
  color: 'var(--pf-t--global--text--color--subtle)',
  marginBlockEnd: 'var(--pf-t--global--spacer--sm)'
};

/**
 * What a page shows when the address exists but this user may not have it: a bookmark kept after
 * a role was taken away, a link passed on by somebody who does hold the role, an address typed by
 * hand.
 *
 * Says so plainly rather than pretending the page is not there. A 404 for something that does
 * exist sends the reader looking for a typo they will never find, and hiding the existence of the
 * Administration section buys nothing - the section is documented, and the service is what
 * actually refuses the data.
 *
 * Deliberately not an error state: nothing failed, and there is nothing to retry. Compare
 * NotFoundState, for an address that matches no record, and DataLoadError, for a request that
 * genuinely failed.
 */
const ForbiddenState: React.FunctionComponent<ForbiddenStateProps> = ({ titleText, children }) => {
  const navigate = useNavigate();

  return (
    <PageSection hasBodyWrapper={false} isFilled>
      {/* The height is asked for rather than inherited, for the reason given in NotFoundState:
          the layout's main is not a flex column, so isFilled alone would leave this tucked
          under the breadcrumb instead of in the middle of the page. */}
      <Bullseye style={{ minHeight: '60vh' }}>
        <EmptyState headingLevel="h1" icon={LockIcon} variant="lg" titleText={
          <>
            <span style={codeStyle}>Error 403</span>
            {titleText ?? 'Access denied'}
          </>
        }>
          <EmptyStateBody>
            {children ?? (
              <>
                You don&apos;t have permission to view this page. If you need it, ask an
                administrator to grant you the role it requires.
              </>
            )}
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => navigate('/')}>Take me home</Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </Bullseye>
    </PageSection>
  );
};

export { ForbiddenState };
